import {
  Button, Checkbox, Drawer, DrawerBody, DrawerContent, DrawerFooter, DrawerHeader, Radio, RadioGroup,
  Select, SelectItem, Textarea,
} from '@heroui/react';
import { Send } from 'lucide-react';
import React, { FC, useState } from 'react';

import { UserSelector } from '../../../../components';
import { useHeroToast } from '../../../../hooks';
import {
  CampaignTarget, SendCampaignResult, useGetOrganizationsRealtime, useSendInMailCampaign,
} from '../../../../queries';
import type { EmailDocument } from '../model/types';
import { documentToPlainText } from '../render/documentToPlainText';
import { renderEmailHtml } from '../render/renderEmailHtml';

/** Stała referencja: nowa tablica przy każdym renderze kasowałaby zaznaczenie w selektorze. */
const EMPTY_KEYS: string[] = [];

type Mode = 'users' | 'organizations' | 'emails' | 'all';

/** Adresy rozdzielone przecinkiem, średnikiem albo nową linią. */
const parseEmails = (value: string): string[] => value
  .split(/[\s,;]+/)
  .map((item) => item.trim().toLowerCase())
  .filter((item) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(item));

interface SendCampaignDrawerProps {
  doc: EmailDocument;
  templateId: string | null;
  onClose: () => void;
}

export const SendCampaignDrawer: FC<SendCampaignDrawerProps> = ({ doc, templateId, onClose }) => {
  const { showError, showSuccess } = useHeroToast();
  const { organizations } = useGetOrganizationsRealtime();
  const { mutateAsync: send, isPending } = useSendInMailCampaign();

  const [mode, setMode] = useState<Mode>('users');
  const [userIds, setUserIds] = useState<string[]>([]);
  const [organizationIds, setOrganizationIds] = useState<string[]>([]);
  const [emailsInput, setEmailsInput] = useState('');
  const [inApp, setInApp] = useState(true);
  const [email, setEmail] = useState(true);
  const [result, setResult] = useState<SendCampaignResult | null>(null);

  const typedEmails = parseEmails(emailsInput);

  const buildTarget = (): CampaignTarget | null => {
    if (mode === 'all') return { mode: 'all' };
    if (mode === 'users') return userIds.length ? { mode: 'users', userIds } : null;
    if (mode === 'emails') return typedEmails.length ? { mode: 'emails', emails: typedEmails } : null;
    return organizationIds.length ? { mode: 'organizations', organizationIds } : null;
  };

  const handleSend = async () => {
    const target = buildTarget();
    if (!target) {
      showError('Wskaż, do kogo ma pójść wiadomość.');
      return;
    }
    if (!inApp && !email) {
      showError('Wybierz przynajmniej jeden kanał.');
      return;
    }
    if (!doc.subject.trim()) {
      showError('Uzupełnij temat wiadomości w pasku kreatora.');
      return;
    }

    try {
      const response = await send({
        subject: doc.subject,
        html: renderEmailHtml(doc),
        text: documentToPlainText(doc),
        channels: { inApp, email },
        target,
        templateId,
        templateName: doc.name,
      });
      setResult(response);
      showSuccess(`Wysłano do ${response.recipientCount} odbiorców.`);
    } catch (error) {
      /* Backend odsyła powód w kopercie `{success, message}` i to jego trzeba pokazać:
         samo „nie powiodło się" zostawiało w konsoli gołe 400 bez informacji, czego
         zabrakło (tematu, kanału, odbiorców). */
      const { response } = (error as { response?: { data?: { message?: string } } });
      showError(response?.data?.message || 'Wysyłka się nie powiodła.');
    }
  };

  return (
    /* Szuflada na pół ekranu zamiast okna modalnego: konfiguracja wysyłki to kilka
       kroków (kanały, tryb, lista osób albo hoteli), a lista użytkowników sama w sobie
       jest przewijana. W oknie modalnym wszystko to ściskało się w wąskiej kolumnie. */
    <Drawer
      isOpen
      placement="right"
      radius="none"
      classNames={{ base: 'w-full sm:w-1/2 sm:max-w-none' }}
      onClose={onClose}
    >
      <DrawerContent>
        <DrawerHeader className="flex-col items-start gap-1">
          <span className="text-base font-semibold text-gray-900">Wyślij wiadomość</span>
          <span className="text-xs font-normal text-gray-500">
            Wybierz kanały i odbiorców. Treść bierzemy z kreatora.
          </span>
        </DrawerHeader>
        <DrawerBody className="gap-6">
          <div className="rounded-lg bg-gray-50 px-3 py-2">
            <p className="text-xs text-gray-500">Temat</p>
            <p className="text-sm font-medium text-gray-900">{doc.subject}</p>
          </div>

          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Kanały</p>
            <Checkbox isSelected={inApp} onValueChange={setInApp}>
              W panelu (skrzynka użytkownika)
            </Checkbox>
            <Checkbox isSelected={email} onValueChange={setEmail}>
              E-mail
            </Checkbox>
            <p className="text-xs text-gray-500">
              Można zaznaczyć oba. Otwarcia liczymy tylko dla e-maila.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Odbiorcy</p>
            <RadioGroup value={mode} onValueChange={(value) => setMode(value as Mode)}>
              <Radio value="users">Wybrane osoby</Radio>
              <Radio value="organizations">Wszyscy z wybranych hoteli</Radio>
              <Radio value="emails">Wpisane adresy e-mail</Radio>
              <Radio value="all">Wszyscy użytkownicy panelu</Radio>
            </RadioGroup>

            {mode === 'users' && (
              <UserSelector onUserSelectionChange={setUserIds} defaultSelectedKey={EMPTY_KEYS} />
            )}

            {mode === 'organizations' && (
              <Select
                label="Hotele"
                selectionMode="multiple"
                selectedKeys={organizationIds}
                onSelectionChange={(keys) => setOrganizationIds(Array.from(keys).map(String))}
              >
                {(organizations ?? []).map((organization) => (
                  <SelectItem key={organization.id}>{organization.name}</SelectItem>
                ))}
              </Select>
            )}

            {mode === 'emails' && (
              <div className="flex flex-col gap-2">
                <Textarea
                  minRows={4}
                  label="Adresy"
                  placeholder="jan@hotel.pl, anna@resort.pl"
                  description="Rozdziel przecinkiem, średnikiem albo nową linią."
                  value={emailsInput}
                  onValueChange={setEmailsInput}
                />
                <p className="text-xs text-gray-500">
                  {typedEmails.length > 0
                    ? `Rozpoznane adresy: ${typedEmails.length}.`
                    : 'Jeszcze nie rozpoznaliśmy żadnego adresu.'}
                  {' Adres, który ma konto w panelu, dostanie wiadomość także do skrzynki.'}
                </p>
              </div>
            )}

            {mode === 'all' && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-700">
                Wiadomość pójdzie do każdego konta panelu z adresem e-mail. Konta serwisowe są pomijane.
              </p>
            )}
          </div>

          {result && (
            <div className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700">
              {`Odbiorcy: ${result.recipientCount}. Wysłane maile: ${result.sentCount}.`}
              {result.failedCount > 0 ? ` Nieudane: ${result.failedCount}.` : ''}
              {result.inboxCount > 0 ? ` W panelu: ${result.inboxCount}.` : ''}
            </div>
          )}
        </DrawerBody>
        <DrawerFooter>
          <Button variant="light" onPress={onClose}>
            {result ? 'Zamknij' : 'Anuluj'}
          </Button>
          <Button
            color="primary"
            isLoading={isPending}
            startContent={<Send className="h-4 w-4" />}
            onPress={handleSend}
          >
            Wyślij
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
